/* eslint-disable @next/next/no-img-element */
"use client";

import OfficeLocation from "@/common/OfficeLocation";
import HomeCard from "@/components/Home/HomeCard";
import MiniBarChart from "@/components/Home/MiniBarChart";
import Layout from "@/components/Layout/Layout";
import AddNewVisit from "@/components/Visitors/Timeline/AddNewVisit";
import { CMSModal } from "@/context";
import Button from "@/designs/Button";
import {
  useGetBookingSpacesQuery,
  useGetDeliveriesQuery,
  useGetEmployeesTimelineQuery,
  useGetVistorsQuery,
} from "@/graphql/generated/schema";
import { Avatar, Card, Timeline } from "antd";
import moment from "moment";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
const last7Days = Array.from({ length: 7 }, (_, i) =>
  moment().subtract(6 - i, "days"),
);

const HomePage = () => {
  const { t } = useTranslation();
  const { user, selectedLocation } = useContext(CMSModal);
  const [addVisit, setAddVisit] = useState(false);

  const startDate = moment().subtract(6, "days").startOf("day").toISOString();
  const endDate = moment().endOf("day").toISOString();

  /* ---------------- VISITORS COUNT ---------------- */

  const { data, loading: visitorLoading } = useGetVistorsQuery({
    variables: {
      location: selectedLocation,
      startDate,
      endDate,
    },
  });
  const visitorsData = data?.getVistors?.visitor;

  const visitorIn =
    visitorsData?.filter((v) => v?.signedType === "In").length || 0;

  const visitorLast7Days = last7Days.map(
    (day) =>
      (visitorsData ?? []).filter((v) => moment(v?.signedIn).isSame(day, "day"))
        .length,
  );

  /* ---------------- EMPLOYEES ---------------- */

  const { data: employee, loading: employeeLoading } =
    useGetEmployeesTimelineQuery({
      variables: {
        location: selectedLocation,
        startDate,
        endDate,
      },
    });

  const employeeData = employee?.getEmployeesTimeline?.employee;
  const employeeIn =
    employeeData?.filter((e) => e?.signedType === "In").length || 0;
  const employeeLast7Days = last7Days.map(
    (day) =>
      (employeeData ?? []).filter((e) => moment(e?.signedIn).isSame(day, "day"))
        .length,
  );
  // Remote employees
  const remoteEmployeeData = employeeData?.filter(
    (e) => e?.signedType === "Remote",
  );

  const remoteEmployeeIn = remoteEmployeeData?.length || 0;

  const remoteEmployeeLast7Days = last7Days.map(
    (day) =>
      (remoteEmployeeData ?? []).filter((e) =>
        moment(e?.signedIn).isSame(day, "day"),
      ).length,
  );
  /* ---------------- BOOKINGS ---------------- */

  const { data: bookings, loading: bookingLoading } = useGetBookingSpacesQuery({
    variables: {
      location: selectedLocation,
      start: startDate,
      end: endDate,
    },
  });

  const bookingData = bookings?.getBookingSpaces;
  const todayBookingCount =
    bookingData?.filter((b) =>
      moment(b?.start).isSame(moment(), "day"),
    ).length || 0;

  const bookingLast7Days = last7Days.map(
    (day) =>
      (bookingData ?? []).filter((b) => moment(b?.start).isSame(day, "day"))
        .length,
  );

  /* ---------------- DELIVERIES ---------------- */

  const { data: deliveries, loading: deliveryLoading } = useGetDeliveriesQuery({
    variables: {
      location: selectedLocation,
      startDate,
      endDate,
    },
  });
  const deliveryData = deliveries?.getDeliveries?.delivery;
  const todayDeliveryCount =
    deliveryData?.filter((d) => {
      const deliveryDate = moment(d?.createdAt);
      return deliveryDate.isSame(moment(), "day");
    }).length || 0;

  const deliveryLast7Days = last7Days.map(
    (day) =>
      (deliveryData ?? []).filter((d) =>
        moment(d?.createdAt).isSame(day, "day"),
      ).length,
  );
  /* ---------------- VISITOR TIMELINE ---------------- */

  const { data: visitorTimelineData } = useGetVistorsQuery({
    variables: {
      location: selectedLocation,
      limit: 20,
      offset: 0,
    },
  });

  const visitorTimeline = visitorTimelineData?.getVistors?.visitor;
  const { theme } = useContext(CMSModal);
  const router = useRouter();
  return (
    <Layout>
      <div
        className={twMerge(
          "p-10",
          theme === "dark"
            ? "bg-[#262727] text-gray-100"
            : "bg-gray-100 text-[#3b3b3b]",
        )}
      >
        <div
          className={twMerge(
            "space-y-6 rounded-lg p-10",
            theme === "dark"
              ? "bg-[#191919] text-gray-100"
              : "bg-white text-[#3b3b3b]",
          )}
        >
          {/* HEADER */}
          <div className={twMerge("text-center space-y-4")}>
            <h2 className="text-xl lg:text-2xl font-bold">
              {t("home.welcome")} {user.firstName} {user.lastName}
            </h2>

            <h4 className="text-xl">
              {t("home.latestAt")} <OfficeLocation />
            </h4>
          </div>
          {/* DASHBOARD CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <HomeCard
              value={visitorIn}
              title={t("home.visitorsIn")}
              subtitle={t("home.past7Days")}
              loading={visitorLoading}
              navigation="/visitors/timeline?status=In"
            >
              <MiniBarChart data={visitorLast7Days} />
            </HomeCard>

            {/* Expected Visitors */}
            <div
              className={twMerge(
                "rounded-xl border border-gray-200 p-6 w-full flex flex-col",
                theme === "dark"
                  ? "bg-[#191919] text-gray-100"
                  : "bg-white text-[#3b3b3b]",
              )}
            >
              <div className="text-center">
                <h2 className="text-2xl font-medium">
                  {t("home.expectedVisitors")}
                </h2>
                <p className="text-gray-500 mt-1 text-sm">
                  {t("home.expectedVisitorsDesc")}
                </p>
              </div>

              <div className="flex-1" />

              <div className="h-px bg-gray-200 mb-4" />

              <div className="flex justify-center">
                <Button variant="outline" onClick={() => router.push('/visitors/preregister')}>
                  {t("home.getStarted")}
                </Button>
              </div>
            </div>

            <HomeCard
              value={employeeIn}
              title={t("home.employeesIn")}
              subtitle={t("home.past7Days")}
              loading={employeeLoading}
              navigation="/employees/timeline?status=In"
            >
              {" "}
              <MiniBarChart data={employeeLast7Days} />
            </HomeCard>
            <HomeCard
              value={remoteEmployeeIn}
              title={t("home.remoteEmployeesIn")}
              subtitle={t("home.past7Days")}
              loading={employeeLoading}
              navigation="/employees/timeline?status=Remote"
            >
              <MiniBarChart data={remoteEmployeeLast7Days} />
            </HomeCard>
            <HomeCard
              value={todayBookingCount}
              title={t("home.bookingsToday")}
              subtitle={t("home.past7Days")}
              loading={bookingLoading}
              navigation="/spaces/bookings"
            >
              <MiniBarChart data={bookingLast7Days} />
            </HomeCard>
            <HomeCard
              value={todayDeliveryCount}
              title={t("home.deliveriesIn")}
              subtitle={t("home.past7Days")}
              loading={deliveryLoading}
              navigation="/delivery"
            >
              <MiniBarChart data={deliveryLast7Days} />
            </HomeCard>
          </div>
          <div
            className={twMerge(
              " p-6 rounded-lg space-y-10",
              theme === "dark"
                ? "bg-[#262727] text-gray-100"
                : "bg-gray-100 text-[#3b3b3b]",
            )}
          >
            {/* Header */}
            <div
              className={twMerge(
                "flex flex-wrap items-center justify-between gap-4",
              )}
            >
              <h2 className="text-xl lg:text-2xl font-semibold">
                {t("home.latestActivity")}
              </h2>

              <div className="flex flex-wrap items-center gap-4 text-sm">
                <Link
                  href="/visitors/timeline"
                  className="font-medium text-[#099ed1] hover:text-[#1bbae1]"
                >
                  {t("home.visitorTimeline")}
                </Link>

                <Link
                  href="/employees/timeline"
                  className="font-medium text-[#099ed1] hover:text-[#1bbae1]"
                >
                  {t("home.employeeTimeline")}
                </Link>
                <Button className="w-30" onClick={() => setAddVisit(true)}>
                  {t("visitors.timeline.newVisit")}
                </Button>
              </div>
            </div>

            {/* Timeline */}
            <Timeline
              items={visitorTimeline?.map((visitor) => ({
                color: "gray",
                content: (
                  <Card
                    className={twMerge(
                      "shadow-sm cursor-pointer absolute bottom-6",
                      "transition-all duration-400",
                      "hover:shadow-md hover:scale-[1.02]",
                    )}
                    styles={{ body: { padding: "12px 16px" } }}
                    onClick={() =>
                      router.push(`/visitors/timeline?emp=${visitor?.id}`)
                    }
                  >
                    <div className="flex items-center justify-between">
                      {/* Text */}
                      <div className=" flex items-center gap-6 text-sm">
                        <Avatar src={visitor?.img || "/Avatar.svg"} size={60} />

                        <div className="flex flex-col gap-2">
                          <span>
                            <b>{visitor?.data?.fullName}</b>{" "}
                            {visitor?.signedType === "Out"
                              ? "signed out of"
                              : "signed in to"}{" "}
                            {visitor?.location?.name}
                            {visitor?.signedType === "Out"
                              ? visitor?.signedOutDevice
                                ? ` - ${visitor.signedOutDevice}`
                                : ""
                              : visitor?.signedInDevice
                                ? ` - ${visitor.signedInDevice}`
                                : ""}
                          </span>
                          <span className="text-gray-500">
                            {visitor?.signedType === "Out"
                              ? moment(visitor?.signedOut).format(
                                  "h:mma, Do MMMM",
                                )
                              : moment(visitor?.signedIn).format(
                                  "h:mma, Do MMMM",
                                )}
                          </span>
                        </div>
                      </div>

                      {/* Tag */}
                      <Button
                        variant="outline"
                        className="w-30 h-8 rounded-full"
                      >
                        {visitor?.data?.type || t("home.visitor")}
                      </Button>
                    </div>
                  </Card>
                ),
              }))}
            />
            <div className="flex flex-wrap items-center gap-4 pl-6 text-sm">
              <Link
                href="/visitors/timeline"
                className="font-medium text-[#099ed1] hover:text-[#1bbae1]"
              >
                {t("home.visitorTimeline")}
              </Link>

              <Link
                href="/employees/timeline"
                className="font-medium text-[#099ed1] hover:text-[#1bbae1]"
              >
                {t("home.employeeTimeline")}
              </Link>
            </div>
            <AddNewVisit isOpen={addVisit} onClose={() => setAddVisit(false)} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
